import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { ConsumerOfferDetail, PaymentEntryItem, SalesEntryProvider, useSalesEntry } from "contexts/SalesEntryContext";
import { Column, DataGrid, Scrolling } from "devextreme-react/data-grid";
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState, } from "react";
import { Button, Card, Col, Modal, Row } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { mapSalesEntryToAPI, validateSalesEntryData } from "utils/sales-entry-mapper";
import { useDiscountVoucherValidation, useProductDetailsSearch, useProductDropdown, useSalesEntrySubmission } from "../services";
import SalesEntryErrorBoundary from "./SalesEntryErrorBoundary";
import { SalesEntryModals } from "./SalesEntryModals";
import { SalesEntrySummary } from "./SalesEntrySummary";
import { SalesEntryTabs } from "./SalesEntryTabs";
import CalculationModal from "./sections/CalculationModal";
import { ExchangeModal } from "./sections/ExchangeModal";
import { ProductSelectionModal } from "./sections/ProductSelectionModal";
import SubmitConfirmationModal from "./sections/SubmitConfirmationModal";
import notistack from "modules/core/notistack";
import ConfirmActionModal from "./sections/ConfirmActionModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import { getSavedCartDetailsByCartId, saveToCart } from "../services/api.services";
import { CouponData } from "./sections/CouponModal";
import { calculateExchangeSaleOrderPrices } from "pages/Components/API_Modal/SalesEntryListAPI/SalesEntryListAPIModal";
import { HttpStatus } from "modules/core";
import { HasPermission } from "Layout/Permissions/permissionsCache";

// Types
type SearchType = "bagNo" | "styleNo" | "igiNo" | "huid";

interface ProductOption {
  text?: string;
  label?: string;
  value?: string;
}

interface ProductSearchField {
  type: SearchType;
  label: string;
  apiType: SearchType;
  dataKey: string;
}

interface ProductAutocompleteProps {
  label: string;
  options: ProductOption[];
  value: string;
  onChange: (value: string | null) => void;
  loading?: boolean;
}

// Constants
const SEARCH_TYPE_MAP: Record<SearchType, 0 | 1 | 2 | 3> = {
  bagNo: 0,
  styleNo: 1,
  igiNo: 2,
  huid: 3,
};

const SEARCH_FIELDS: ProductSearchField[] = [
  { type: "bagNo", label: "Bag No.", apiType: "bagNo", dataKey: "bagNoList" },
  { type: "styleNo", label: "Style No.", apiType: "styleNo", dataKey: "styleNoList" },
  { type: "igiNo", label: "IGI No.", apiType: "igiNo", dataKey: "igiNoList" },
  { type: "huid", label: "HUID", apiType: "huid", dataKey: "huidList" },
];

// Utility Functions
export const extractConsumerOfferDetails = (product: unknown, productIndex?: number): any[] => {
  const p = product as Record<string, unknown>;
  if (!p.consumerOfferDetail || !Array.isArray(p.consumerOfferDetail)) {
    return [];
  }

  return p.consumerOfferDetail.map((offer: any, index: number) => ({
    id: `${p.id}_${offer.id}_${productIndex || 0}_${index}`,
    productId: String(p.id || ''),
    discountOnSRPOptionId: Number(offer.discountOnSRPOptionId || 0),
    discountTypeId: Number(offer.discountTypeId || 0),
    discountValue: Number(offer.discountValue || offer.discountAmount || 0),
    isPercentage: Boolean(offer.isPercentage || false),
    fromDate: String(offer.fromDate || ''),
    toDate: String(offer.toDate || ''),
    isStyleNo: Boolean(offer.isStyleNo || false),
    styleNumber: String(offer.styleNumber || offer.styleNo || p.styleNo || ''),
    isDefault: Boolean(offer.isDefault),
    promotionName: String(offer.promotionName || offer.name || offer.offerName || ''),
    invoiceRangeFrom: Number(offer.invoiceRangeFrom || 0),
    invoiceRangeTo: Number(offer.invoiceRangeTo || 0),
    isActive: Boolean(offer.isActive !== false), // Default to true if not specified
    materialTypeId: Number(offer.materialTypeId || 0),
    materialName: String(offer.materialName || ''),
    discountOnSRPNmae: String(offer.discountOnSRPNmae || offer.discountOnSRPName || ''), // Keep original typo
    discountTypeNmae: String(offer.discountTypeNmae || offer.discountTypeName || ''), // Keep original typo
    discountOnMRPName: String(offer.discountOnMRPName || offer.discountOnMRPName || ''), // Keep original typo
    isSelected: false, // Auto-select if default
    styleNo: String(p.styleNo || ''),
    bagNo: String(p.bagNo || ''),
    // Legacy fields for backward compatibility - these are not part of the interface but useful for display
    offerId: String(offer.id || ''),
    offerName: String(offer.promotionName || offer.name || offer.offerName || ''),
    offerType: String(offer.type || offer.offerType || ''),
    discountPercentage: Number(offer.discountPercentage || 0),
    consumerOfferOn: String(offer.consumerOfferOn),
    consumerOfferId: String(offer.consumerOfferId),
    isClub: String(offer.isClub),
  }));
};

export const extractConsumerOfferDetailsFromArray = (
  offers: any[],
  productIndex = 0
): ConsumerOfferDetail[] => {
  if (!Array.isArray(offers)) {
    return [];
  }

  return offers.map((offer: any, index: number): ConsumerOfferDetail => ({
    id: `${offer.id || "o"}`, // unique id for UI toggle
    productId: String(offer.productId || ""),
    discountOnSRPOptionId: Number(offer.discountOnSRPOptionId || 0),
    discountTypeId: Number(offer.discountTypeId || 0),
    discountValue: Number(offer.discountValue ?? offer.discountAmount ?? 0),
    isPercentage: Boolean(offer.isPercentage || false),
    fromDate: String(offer.fromDate || ""),
    toDate: String(offer.toDate || ""),
    isStyleNo: Boolean(offer.isStyleNo || false),
    styleNumber: String(offer.styleNumber || offer.styleNo || ""),
    isDefault: Boolean(offer.isDefault),
    promotionName: String(offer.promotionName || offer.name || offer.offerName || ""),
    invoiceRangeFrom: Number(offer.invoiceRangeFrom || 0),
    invoiceRangeTo: Number(offer.invoiceRangeTo || 0),
    isActive: Boolean(offer.isActive !== false),
    materialTypeId: Number(offer.materialTypeId || 0),
    materialName: String(offer.materialName || ""),
    discountOnSRPNmae: String(offer.discountOnSRPNmae || offer.discountOnSRPName || ""),
    discountTypeNmae: String(offer.discountTypeNmae || offer.discountTypeName || ""),
    discountOnMRPName: String(offer.discountOnMRPName || ""),
    isSelected: false, // better default, reducer handles toggle
    styleNo: String(offer.styleNo || ""),
    bagNo: String(offer.bagNo || ""),
    originalValue: 0, // will be calculated when toggled
    consumerOfferOn: String(offer.consumerOfferOn || ""),
    consumerOfferId: String(offer.consumerOfferId || ""),
    isClub: Boolean(offer.isClub || false),
  }));
};



const mapProductToItem = (product: unknown) => {
  const p = product as Record<string, unknown>;
  return {
    id: `${p.id}` || '',
    image: String(p.image || ""),
    styleNo: String(p.styleNo || ""),
    category: String(p.category || ""),
    diaGoldWt: Number(p.diaWt) || 0,
    diaQty: Number(p.diaQty) || 0,
    diaClarity: String(p.diaClarity || ""),
    diaColor: String(p.diaColor || ""),
    netWt: Number(p.netWt) || 0,
    grossWt: Number(p.grossWt) || 0,
    kt: String(p.karatType) || "",
    ktId: Number(p.metalKtId),
    karatType: String(p.karatType) || "",
    pcs: Number(p.pcs) || 0,
    igiNo: String(p.certificateNo || ""),
    huidNo: String(p.huid || ""),
    bagNo: String(p.bagNo || ""),
    mrp: Number(p.mrpPrice) || 0,
    tag: String(p.tag || ""),
    isSNMCC: Boolean(p.isSNMCC),
    isSRP: Boolean(p.isSRP),
    color: String(p.materialColorName || ""),
    goldMrp: Number(p.goldMrp) || 0,
    diaMrp: Number(p.diaMrp) || 0,
    categoryName: String(p.categoryName || ""),
    making: Number(p.making) || 0,
    makingMrp: Number(p.makingMrp) || 0,
    materialTypeName: String(p.materialTypeName) || "",
    complexityName: String(p.complexityName) || "",
    complexityPercentage: Number(p.complexityPercentage) || 0,
    metalAmount: Number(p.metalAmount) || 0,
    colorStonePrice: Number(p.colorStonePrice) || 0,
    subMrp: Number(p.subMrp) || 0,
    labMrp: Number(p.labMrp) || 0,
    mrpPrice: Number(p.mrpPrice) || 0,
    makingCharges: Number(p.makingCharges) || 0,
    materialDiscount: Number(p.materialDiscount) || 0,
    diamondDiscount: Number(p.diamondDiscount) || 0,
    labDiscount: Number(p.labDiscount) || 0,
    mrpDiscount: Number(p.mrpDiscount) || 0,
    todaysGoldRate: Number(p.todaysGoldRate) ?? 0,
    materialTypeId: String(p.materialTypeId) ?? ''
  };
};

// Loading Overlay Component
const LoadingOverlay: React.FC<{ show: boolean; title: string; message: string }> = ({
  show,
  title,
  message
}) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
      }}
    >
      <div
        className="bg-white p-4 rounded shadow-lg text-center"
        style={{ minWidth: '250px' }}
      >
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h6 className="mb-2">{title}</h6>
        <p className="text-muted mb-0">{message}</p>
      </div>
    </div>
  );
};

// Modal state management
type ModalState = {
  [K in
  | "customer"
  | "salesPerson"
  | "redemption"
  | "rewardPoints"
  | "coupon"
  | "discount"
  | "payment"
  | "oldGold"
  | "exchange"
  | "calculation"
  | "submitConfirmation"
  | "productSelection"]: boolean;
};

type ModalAction =
  | {
    type: "TOGGLE_MODAL";
    payload: { name: keyof ModalState; isOpen?: boolean };
  }
  | { type: "CLOSE_ALL_MODALS" };

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case "TOGGLE_MODAL":
      return {
        ...state,
        [action.payload.name]:
          action.payload.isOpen ?? !state[action.payload.name],
      };
    case "CLOSE_ALL_MODALS":
      return Object.keys(state).reduce(
        (acc, key) => ({ ...acc, [key]: false }),
        {} as ModalState
      );
    default:
      return state;
  }
};

const initialModalState: ModalState = {
  customer: false,
  salesPerson: false,
  redemption: false,
  rewardPoints: false,
  coupon: false,
  discount: false,
  payment: false,
  oldGold: false,
  exchange: false,
  calculation: false,
  submitConfirmation: false,
  productSelection: false,
};

// Memoized Components
const ProductAutocomplete = React.memo<ProductAutocompleteProps>(
  ({ label, options, value, onChange, loading = false }) => {
    const getOptionLabel = useCallback(
      (option: ProductOption | string): string => {
        if (typeof option === "string") return option;
        return option?.text || option?.label || "";
      },
      []
    );

    const handleChange = useCallback(
      (_: any, newValue: ProductOption | string | null) => {
        if (typeof newValue === "string") {
          onChange(newValue);
        } else if (newValue) {
          onChange(newValue.text || newValue.label || null);
        } else {
          onChange(null);
        }
      },
      [onChange]
    );

    return (
      <Autocomplete
        disablePortal
        options={options}
        value={value || null}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}

        isOptionEqualToValue={(option, value) =>
          getOptionLabel(option) === getOptionLabel(value)
        }
        size="small"
        sx={{
          width: "100%",
          "& .MuiInputBase-root": { fontSize: "0.875rem" },
          "& .MuiInputLabel-root": { fontSize: "0.875rem" },
          "& .MuiAutocomplete-listbox": {
            fontSize: "0.875rem",
            padding: "4px 0",
          },
          "& .MuiAutocomplete-option": { fontSize: "0.875rem" },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            variant="standard"


            slotProps={{
              select: {
                native: true,
              },
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    );
  }
);

const ProductSearchSection = React.memo<{
  allProductIds: any;
  state: any;
  loading: boolean;
  onProductSelect: (value: string | null, type: SearchType) => void;
}>(({ allProductIds, state, loading, onProductSelect }) => (
  <Row className="mb-4">
    <Col sm={12}>
      <Row>
        {SEARCH_FIELDS.map(({ type, label, apiType, dataKey }) => (
          <Col sm={3} key={type}>
            <ProductAutocomplete
              label={label}
              options={
                (allProductIds as Record<string, ProductOption[]>)?.[dataKey] ?? []
              }
              value={state[type]}
              onChange={(newValue) => onProductSelect(newValue, apiType)}
              loading={loading}
            />
          </Col>
        ))}
      </Row>
    </Col>
  </Row>
));

// Display names for better debugging
ProductAutocomplete.displayName = 'ProductAutocomplete';
ProductSearchSection.displayName = 'ProductSearchSection';
LoadingOverlay.displayName = 'LoadingOverlay';
const NewSalesEntryContent: React.FC = () => {
  const { state, actions, coid } = useSalesEntry();
  const [productsToCheck, setProductsToCheck] = useState<any[]>([])
  const navigate = useNavigate();

  useEffect(() => {
    if (JSON.stringify(state?.items) !== JSON.stringify(productsToCheck)) {
      setProductsToCheck([...state?.items]);
    }
  }, [state.items]);

  // Optimized modal state management
  const [modals, dispatch] = useReducer(modalReducer, initialModalState);

  // Product selection state - only when needed
  const [productSelectionData, setProductSelectionData] = useState<{
    products: unknown[];
    searchType: SearchType;
  }>({ products: [], searchType: "bagNo" });

  // Loading overlay state for product selection
  const [showProductLoadingOverlay, setShowProductLoadingOverlay] = useState(false);

  const {
    allProductIds,
    loading: productLoading,
    error: productError,
  } = useProductDropdown();
  const {
    loading: productDetailsLoading,
    error: productDetailsError,
    getProductDetailsForSalesEntry,
  } = useProductDetailsSearch();
  const {
    loading: submissionLoading,
    error: submissionError,
    createSalesEntry,
  } = useSalesEntrySubmission();

  // Optimized modal control
  const toggleModal = useCallback(
    (name: keyof ModalState, isOpen?: boolean) => {
      dispatch({ type: "TOGGLE_MODAL", payload: { name, isOpen } });
    },
    []
  );

  // Clear all search fields utility
  const clearAllSearchFields = useCallback(() => {
    actions.setBagNo("");
    actions.setStyleNo("");
    actions.setIgiNo("");
    actions.setHuid("");
  }, [actions]);

  // Check if items exist for modal validation
  const requiresItems = useCallback(() => {
    if (state?.items?.length === 0) {
      notistack.warning("No Product Found, Please Check!");
      return false;
    }
    return true;
  }, [state?.items?.length]);

  // Memoized tab handlers for better performance
  const tabHandlers = useMemo(
    () => ({
      onCustomerClick: () => requiresItems() && toggleModal("customer", true),
      onSalesPersonClick: () => requiresItems() && toggleModal("salesPerson", true),
      onCouponClick: () => requiresItems() && toggleModal("coupon", true),
      onDiscountClick: () => requiresItems() && toggleModal("discount", true),
      onPaymentClick: () => requiresItems() && toggleModal("payment", true),
      onOldGoldClick: () => requiresItems() && toggleModal("oldGold", true),
      onExchangeClick: () => requiresItems() && toggleModal("exchange", true),
      onRedemptionClick: () => requiresItems() && toggleModal("redemption", true),
      onRewardPointsClick: () => requiresItems() && toggleModal("rewardPoints", true),
      onCalculationClick: () => requiresItems() && toggleModal("calculation", true),
    }),
    [toggleModal, requiresItems]
  );

  useEffect(() => {
    [productError, productDetailsError, submissionError].forEach((error) => {
      if (error) {
        toast.error(error, {
          theme: "colored",
        });
        // clear the selected product keys
      }
    });
  }, [productError, productDetailsError, submissionError]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "F10") {
        event.preventDefault();
        toggleModal("calculation", true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleModal]);

  // Event handlers
  const handleDeleteItem = useCallback(
    (itemId: string, index: number) => {
      actions.removeItem(itemId, index);
      actions.removeRewardPoints()
      actions.clearRewardRedeemDetails();
      actions.setRewardPoints(0);
      const hasDiscount = state.discount != null && state.discount > 0;
      const isCartEmpty = state.items.length > 0;
      const isOtherCondition = true;

      if (hasDiscount && (isCartEmpty || isOtherCondition)) {
        notistack.warning(
          'The discount has been removed as the products in your cart have changed. Please reapply the discount to continue.'
        );
        actions.setDiscount(0);
      }
    },
    [actions, state.discount, state.items]
  );

  const { items } = state;

  const decodeValue = (encoded: string | undefined): string | undefined => {
    try {
      if (!encoded) return undefined;
      return decodeURIComponent(atob(encoded));
    } catch (error) {
      console.error("Decoding failed for:", encoded, error);
      return undefined;
    }
  };

  const customeOrderId = decodeValue(coid);
  const handleProductSelect = useCallback(
    async (searchValue: string | null, searchType: SearchType) => {

      // Update state
      const stateActions = {
        bagNo: actions.setBagNo,
        styleNo: actions.setStyleNo,
        igiNo: actions.setIgiNo,
        huid: actions.setHuid,
      } as const;

      stateActions[searchType](searchValue || "");

      if (!searchValue?.trim()) return;

      if (state.discount != null && state.discount > 0) {
        notistack.warning('The discount has been removed as the products in your cart have changed. Please reapply the discount to continue.')
        actions.setDiscount(0);
      }

      setShowProductLoadingOverlay(true);

      try {
        actions.removeRewardPoints()
        actions.clearRewardRedeemDetails();
        actions.setRewardPoints(0);
        const productDetails = await getProductDetailsForSalesEntry(
          searchValue.trim(),
          SEARCH_TYPE_MAP[searchType],
          customeOrderId
        );

        if (productDetails?.length) {
          if (productDetails.length === 1) {
            const product = productDetails[0];
            const newItem = mapProductToItem(product);

            let isDuplicate = false;

            if (Array.isArray(items) && items.length > 0) {
              state.items.forEach(p => {
                if (String(p.id) === String(newItem.id)) {
                  isDuplicate = true;
                }
              });
            }

            if (isDuplicate) {
              notistack.warning('Product already exists');
              return
            }
            actions.addItem(newItem);

            if (state.discount != null && state.discount > 0) {
              notistack.warning('The discount has been removed as the products in your cart have changed. Please reapply the discount to continue.')
              actions.setDiscount(0);

            }
            // Add consumer offer details if available
            const offerDetails = extractConsumerOfferDetails(product, 0);
            if (offerDetails.length > 0) {
              actions.addMultipleConsumerOfferDetails(offerDetails);
            }

            toast.success(`Product "${searchValue}" added successfully!`);
          } else {
            setProductSelectionData({ products: productDetails, searchType });
            toggleModal("productSelection", true);
          }
        }
        // Always clear search fields after processing
        clearAllSearchFields();
      } catch (error) {
        toast.error("Failed to fetch product details. Please try again.");
        clearAllSearchFields();
      } finally {
        setShowProductLoadingOverlay(false);
      }
    },
    [actions, getProductDetailsForSalesEntry, toggleModal, clearAllSearchFields, state.items, state.discount]
  );


  const addProductToItems = useCallback(
    (product: unknown) => {
      const newItem = mapProductToItem(product);
      let isDuplicate = false;

      if (Array.isArray(items) && items.length > 0) {
        state.items.forEach(p => {
          if (String(p.id) === String(newItem.id)) {
            isDuplicate = true;
          }
        });
      }

      if (isDuplicate) {
        toast.warn('Product already exists');
        return
      }
      actions.addItem(newItem);
      if (state.discount != null && state.discount > 0) {
        notistack.warning('The discount has been removed as the products in your cart have changed. Please reapply the discount to continue.')
        actions.setDiscount(0);

      }
      actions.removeRewardPoints()
      actions.clearRewardRedeemDetails();

      if (state.discount != null && state.discount > 0) {
        notistack.warning('The discount has been removed as the products in your cart have changed. Please reapply the discount to continue.')
        actions.setDiscount(0);
      }

      const offerDetails = extractConsumerOfferDetails(product, 0);
      if (offerDetails.length > 0) {
        actions.addMultipleConsumerOfferDetails(offerDetails);
      }

      toggleModal("productSelection", false);
      clearAllSearchFields();
    },
    [actions, toggleModal, clearAllSearchFields, state.items]
  );

  const addMultipleProductsToItems = useCallback(
    (products: unknown[]) => {
      const allOfferDetails: any[] = [];
      const addedItems: any[] = [];
      if (state.discount != null && state.discount > 0) {
        notistack.warning('The discount has been removed as the products in your cart have changed. Please reapply the discount to continue.')
        actions.setDiscount(0);
      }
      products.forEach((product, index) => {
        const newItem = mapProductToItem(product);

        let isDuplicate = false;

        if (Array.isArray(state.items) && state.items.length > 0) {
          isDuplicate = state.items.some(p => String(p.id) === String(newItem.id));
        }

        if (isDuplicate) {
          notistack.warning(`Product "${newItem.styleNo || newItem.id}" already exists`);
          return;
        }

        actions.addItem(newItem);
        if (state.discount != null && state.discount > 0) {
          notistack.warning('The discount has been removed as the products in your cart have changed. Please reapply the discount to continue.')
          actions.setDiscount(0);
        }
        actions.removeRewardPoints()
        actions.clearRewardRedeemDetails();
        actions.setRewardPoints(0);
        if (state.discount != null && state.discount > 0) {
          notistack.warning('The discount has been removed as the products in your cart have changed. Please reapply the discount to continue.')
          actions.setDiscount(0);

        }
        addedItems.push(newItem);

        const offerDetails = extractConsumerOfferDetails(product, index);
        if (offerDetails.length > 0) {
          allOfferDetails.push(...offerDetails);
        }
      });

      if (allOfferDetails.length > 0) {
        actions.addMultipleConsumerOfferDetails(allOfferDetails);
      }

      if (addedItems.length > 0) {
        toggleModal("productSelection", false);
        clearAllSearchFields();
        toast.success(`${addedItems.length} product${addedItems.length > 1 ? 's' : ''} added successfully!`);
      }
    },
    [actions, toggleModal, clearAllSearchFields, state.items]
  );

  const handleSave = useCallback(() => {
    if (((state.customer?.BillingAddressId == null || state.customer?.BillingAddressId == undefined) && (state.customer?.ShippingAddressId == null || state.customer?.ShippingAddressId == undefined))) {
      notistack.warning("Please select the billing and shipping address");
      return;
    }

    const validation = validateSalesEntryData(state);

    if (!validation.isValid) {
      validation.errors.forEach((error) => toast.error(error));
      return;
    }

    toggleModal("submitConfirmation", true);
  }, [state, toggleModal]);

  const [showModal, setShowModal] = useState(false);
  const [showEcomModal, setShowEcomModal] = useState(false);
  const [dataTolock, setDataTolock] = useState()
  const [dataToEcomlock, setDataToEcomlock] = useState()
  const [isEcomLocked, setisEcomLocked] = useState(false)
  const handleConfirmSubmit = useCallback(async () => {
    try {
      const apiPayload = mapSalesEntryToAPI(state);

      const result: any = await createSalesEntry(apiPayload);

      if (result.status == 200) {
        toast.success("Sales entry created successfully!");
        actions.resetForm();
        toggleModal("submitConfirmation", false);
        window.history.replaceState({}, document.title);
        navigate("/sales-entry-list", { state: { openFirstInvoice: true } });
      } else if (result.status == 400 && result.message == 'Locked') {
        setShowModal(true)
        toggleModal("submitConfirmation", false);
        setDataTolock(result.data)
      } else if (result.status == 400 && result.message == 'EcomLocked') {
        setShowEcomModal(true)
        toggleModal("submitConfirmation", false);
        setisEcomLocked(true)
        setDataToEcomlock(result.data)
      } else {
        notistack.notifyFromApi(result || "Failed to save sales entry. Please try again.");
        toggleModal("submitConfirmation", false);
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to save sales entry";
      toast.error(errorMessage);
      toggleModal("submitConfirmation", false);
    }
  }, [
    state,
    createSalesEntry,
    actions,
    navigate,
    submissionError,
    toggleModal,
  ]);

  const handleCancel = useCallback(() => {
    actions.resetForm();
  }, [actions]);

  // Memoized modal handlers for performance
  const modalHandlers = useMemo(
    () => ({
      customer: {
        show: modals.customer,
        onHide: (show: boolean) => toggleModal("customer", show),
      },
      salesPerson: {
        show: modals.salesPerson,
        onHide: (show: boolean) => toggleModal("salesPerson", show),
      },
      redemption: {
        show: modals.redemption,
        onHide: (show: boolean) => toggleModal("redemption", show),
      },
      rewardPoints: {
        show: modals.rewardPoints,
        onHide: (show: boolean) => toggleModal("rewardPoints", show),
      },
      coupon: {
        show: modals.coupon,
        onHide: (show: boolean) => toggleModal("coupon", show),
      },
      discount: {
        show: modals.discount,
        onHide: (show: boolean) => toggleModal("discount", show),
      },
      payment: {
        show: modals.payment,
        onHide: (show: boolean) => toggleModal("payment", show),
      },
      oldGold: {
        show: modals.oldGold,
        onHide: (show: boolean) => toggleModal("oldGold", show),
      },
      exchange: {
        show: modals.exchange,
        onHide: (show: boolean) => toggleModal("exchange", show),
      },
    }),
    [modals, toggleModal]
  );

  const [isCartSave, setIsCartSave] = useState(false)


  const handleAddToCart = async () => {
    if (!state.customer) {
      notistack.warning("Please select a customer before saving draft");
      return;
    }

    if (!state.items || state.items.length === 0) {
      notistack.warning("Please add at least one item before saving draft");
      return;
    }

    if (!state.salesPerson?.id.trim()) {
      notistack.warning("Please add sales person before saving draft");
      return;
    }

    const payload = {
      customerId: state.customer.id,
      salesPersonId: state.salesPerson.id,
      salesPersonDetails: state.salesPerson,
      items: state.items,
      coupon: state.coupon,
      consumerOfferDetail: state.consumerOfferDetail,
      BillingAddressId: state.customer.BillingAddressId,
      ShippingAddressId: state.customer.ShippingAddressId,
      paymentDetails: state.payment,
      goldEntries: state.oldGoldEntries,
      discountDetails: {
        discount: state.discount,
        discountType: state.discountType
      },
      exchangeData: {
        exchangeItems: state.exchItem,
        exchCalData: state.exchCalData
      }
    }

    const productHeaderIdList = items.map(item => Number(item.id));

    const payloadToPass = {
      cartDetailJson: JSON.stringify(payload),
      productHeaderIdList: productHeaderIdList,
      customerId: state.customer.id,
      salesPersonId: state.salesPerson.id,
    }
    try {
      setIsCartSave(true)
      const response = await saveToCart(payloadToPass)
      if (response.status == 200) {
        notistack.notifyFromApi(response)
        navigate('/cart-details')
      } else {
        notistack.notifyFromApi(response)
      }
    } catch (err) {
      notistack.error("Failed to save draft");
    } finally {
      setIsCartSave(false)
    }
  };

  const addGiftVoucherPayment = (couponAmount: number, voucherCode: string) => {
    const existingGiftVoucher = state.payment?.find(
      payment => payment.method === 'Gift Voucher' && payment.referenceNumber === voucherCode
    );

    if (!existingGiftVoucher) {
      actions.addPayment({
        method: 'Gift Voucher',
        amount: couponAmount,
        referenceNumber: voucherCode,
        discountAmount: 0,
      });
    }
  };

  const handleCouponSubmit = (coupon: { id: string; code: string; discount: number; couponPrice: number; couponTypeId: number; couponCode: string | null; withGst: boolean | null; isConsumerOfferApplicable: boolean | null; } | null) => {
    actions.setCoupon(coupon);
  };

  const [errorMessage, setErrorMessage] = useState<string>('')
  const [showErr, setShowErr] = useState(false)
  const { validateDiscountVoucher } = useDiscountVoucherValidation();
  const getCartDetails = async () => {
    try {
      const response: any = await getSavedCartDetailsByCartId(location.state.cartId)
      if (response.status === 200 && response.data.products) {
        let parsedCartDetail = null;

        if (response?.data?.cartDetailJson) {
          parsedCartDetail = JSON.parse(response.data.cartDetailJson);
        }

        const addedItems: any[] = [];
        const allOfferDetails: any[] = [];

        response.data.products.forEach((product: any, index: number) => {
          const newItem = mapProductToItem(product);

          const isDuplicate =
            Array.isArray(state.items) &&
            state.items.some((p) => String(p.id) === String(newItem.id));

          if (isDuplicate) {
            // notistack.warning(`Product "${newItem.styleNo || newItem.id}" already exists`);
            return;
          }

          if (state.discount && state.discount > 0) {
            notistack.warning(
              "The discount has been removed as the products in your cart have changed. Please reapply the discount to continue."
            );
            actions.setDiscount(0);
          }
          actions.removeRewardPoints();
          actions.clearRewardRedeemDetails();

          addedItems.push(newItem);
          actions.addItem(newItem);

          const offerDetails = extractConsumerOfferDetails(product, index);
          if (offerDetails.length > 0) {
            allOfferDetails.push(...offerDetails);
          }
        });

        if (allOfferDetails.length > 0) {
          actions.addMultipleConsumerOfferDetails(allOfferDetails);
        }

        if (parsedCartDetail?.salesPersonDetails) {
          actions.setSalesPerson(parsedCartDetail?.salesPersonDetails);
        }

        if (parsedCartDetail?.paymentDetails) {
          actions.clearPayments();
          if (parsedCartDetail?.paymentDetails) {
            parsedCartDetail?.paymentDetails.forEach((payment: PaymentEntryItem) => actions.addPayment(payment));
          }
        }

        if (parsedCartDetail?.goldEntries) {
          const totalAmount = parsedCartDetail?.goldEntries.reduce((sum: any, entry: { totalAmt: any; }) => sum + entry.totalAmt, 0);
          const totalWeight = parsedCartDetail?.goldEntries.reduce((sum: any, entry: { netWt: any; }) => sum + entry.netWt, 0);
          actions.setOldGoldEntries(parsedCartDetail?.goldEntries);
          actions.setOldGold({ amount: totalAmount, weight: totalWeight });
        }

        let voucherAmt: number = 0;
        if (parsedCartDetail?.coupon?.couponCode?.trim()) {
          const voucherDetails = await validateDiscountVoucher(parsedCartDetail?.coupon?.couponCode?.trim(), [...(addedItems.map(item => item.ktId))], state.customer?.id);

          if (voucherDetails && voucherDetails.isActive) {
            const totalAmt = addedItems.reduce((total, item) => total + (item.mrpPrice || 0), 0);

            if (voucherDetails.minInvoiceAmt > totalAmt) {
              toast.warning(`Invoice amount (${totalAmt}) should be greater than the voucher amount (${voucherDetails.minInvoiceAmt}).`);
              return;
            }

            const realOffers = allOfferDetails.filter(offer =>
            ((offer.productId && !offer.productId.startsWith('test_') &&
              state?.items.some(item => item.id === offer.productId))
              || !offer.productId
            ));

            const selectedOffers = realOffers.filter(offer => offer.isSelected) ?? [];

            let discountValue = 0;
            if (voucherDetails.voucherValueIsPercentage) {
              discountValue = (totalAmt * voucherDetails.voucherValue) / 100; // percentage
              voucherAmt = (totalAmt * voucherDetails.voucherValue) / 100;
            } else {
              discountValue = voucherDetails.voucherValue; // fixed amount
              voucherAmt = voucherDetails.voucherValue; // fixed amount
            }

            if (voucherDetails.isConsumerOfferApplicable || selectedOffers.length == 0) {
              const couponData: CouponData = {
                id: voucherDetails.id.toString(),
                code: parsedCartDetail?.coupon?.couponCode?.trim(),
                discount: discountValue,
                couponPrice: discountValue,
                couponTypeId: voucherDetails.id,
                couponCode: parsedCartDetail?.coupon?.couponCode?.trim(),
                withGst: voucherDetails.withGst,
                isConsumerOfferApplicable: voucherDetails.isConsumerOfferApplicable,
              };

              if (voucherDetails?.withGst) {
                addGiftVoucherPayment(discountValue || 0, parsedCartDetail?.coupon?.couponCode?.trim());
              }

              handleCouponSubmit(couponData);
              toast.success(`Coupon code "${parsedCartDetail?.coupon?.couponCode?.trim()}" applied successfully! Discount: Discount:${voucherDetails.voucherValueIsPercentage ? '%' : "₹"}${voucherDetails.voucherValue} added as Gift Voucher payment`);
            }
            else if (selectedOffers.length > 0) {
              toast.warning("This voucher code can't be clubbed with a consumer offer.");
            }
          }
        }
        if (parsedCartDetail?.discountDetails) {
          const { discount, discountType } = parsedCartDetail.discountDetails;
          let ornamentAmt = 0;
          addedItems.forEach(item => {
            const itemAmount = (item.mrp || 0) * (item.pcs || 1);
            ornamentAmt += itemAmount;
          });
          if (discount && discountType && ornamentAmt > 0) {
            let finalDiscount = 0;
            ornamentAmt -= voucherAmt;
            if (discountType === "percentage") {
              const discountPercent = (Number(discount) / ornamentAmt) * 100;
              finalDiscount = (((ornamentAmt * Math.min(discountPercent, 100)) / 100));
            } else {
              finalDiscount = Number(discount);
            }
            if (finalDiscount > ornamentAmt) {
              finalDiscount = ornamentAmt;
            }
            actions.setDiscount(finalDiscount, discountType ?? undefined);
          }
        }
        if (parsedCartDetail?.exchangeData) {
          if (Array.isArray(parsedCartDetail?.exchangeData?.exchangeItems) && parsedCartDetail?.exchangeData?.exchangeItems) {
            if (parsedCartDetail?.exchangeData?.exchCalData) {
              const response = await calculateExchangeSaleOrderPrices(parsedCartDetail?.exchangeData?.exchCalData);
              if (response.status === HttpStatus.Success) {
                const totalExchangeAmount = Array.isArray(parsedCartDetail?.exchangeData?.exchangeItems) && parsedCartDetail?.exchangeData?.exchangeItems?.reduce((sum: number, item: { totalReturnSaleOrderAmount: number; }) => sum + item?.totalReturnSaleOrderAmount, 0);
                const totalExchangeWeight = Array.isArray(parsedCartDetail?.exchangeData?.exchangeItems) && parsedCartDetail?.exchangeData?.exchangeItems?.reduce((sum: number, item: { netWt: number; }) => sum + item?.netWt, 0);

                actions.addExchangeInvoice({
                  invoiceNo: '',
                  customerName: '',
                  items: parsedCartDetail?.exchangeData?.exchangeItems ?? [],
                  totalAmount: totalExchangeAmount ?? 0,
                  totalWeight: totalExchangeWeight ?? 0,
                  totalItems: parsedCartDetail?.exchangeData?.exchangeItems?.length ?? 0
                });
                actions.exchagneSellesReturn(response.data)

                const calculatedExchangeAmount = response?.data?.totalReturnSaleOrderAmount;

                actions.updateVoucherDetails({
                  exchangeAmt: calculatedExchangeAmount,
                });

              } else {
                notistack.notifyFromApi(response)
              }
            }
          }
        }
        actions.setDraftId(location.state.cartId);
      }
      else if (response.status == 400) {
        setErrorMessage(response.message)
        setShowErr(true)
      }
    } catch (error: any) {
      notistack.error(error.message)
    }
  }

  const location = useLocation();
  const calledRef = useRef(false);
  useEffect(() => {
    if (!location?.state?.cartId) return;
    if (calledRef.current) return;
    calledRef.current = true
    getCartDetails();
  }, []);

  return (
    <div className="" style={{ position: 'relative' }}>
      <LoadingOverlay
        show={showProductLoadingOverlay}
        title="Loading Product Details"
        message="Please wait while we fetch the product information..."
      />

      <Modal
        size="sm"
        show={showErr}
        onHide={() => {
          setShowErr(false)
        }}
        centered
        backdrop="static"
      >
        <Modal.Body className="text-center">
          <p className="text-muted mb-4">{errorMessage}</p>
          <div className="d-flex justify-content-center gap-2">
            <button
              className='btn submit-btn'
              onClick={() => setShowErr(false)}
            >
              Ok
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Row>
        <Col sm={9}>
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Create New Sales Entry</h5>
              <div className="d-flex gap-2">
                {state.items && state.customer && (location?.state?.cartId == null || location?.state?.cartId == undefined) && <button className="btn add-btn" onClick={handleAddToCart} disabled={isCartSave}>
                  <FontAwesomeIcon icon={faShoppingCart} /> Add to Draft
                </button>}
              </div>
            </Card.Header>
            <Card.Body className="pt-2">
              {/* Product Search Section */}
              <ProductSearchSection
                allProductIds={allProductIds}
                state={state}
                loading={productLoading || productDetailsLoading}
                onProductSelect={handleProductSelect}
              />

              <SalesEntryTabs {...tabHandlers} />
              {/* Keyboard shortcuts hint */}
              <div className="text-end mb-2 mt-1">
                {/* <small className="text-muted">
                  <i className="bx bx-info-circle me-1"></i>
                  Use F1-F9 keys for quick access to tabs | F10 for View Calculation
                </small> */}
              </div>

              <DataGrid
                dataSource={state.items}
                showBorders
                showColumnLines
                showRowLines
                allowColumnReordering
                allowColumnResizing
                columnAutoWidth
                rowAlternationEnabled
                keyExpr="id"
              >
                <Scrolling
                  mode='standard'
                  useNative={true}
                  scrollByContent={true}
                  scrollByThumb={true}
                />
                {/* <Column
                  caption="Image"
                  dataField="image"
                  width={80}
                  cellRender={() => (
                    <div className="text-center">
                      <i className="bx bx-image-alt text-muted fs-4" />
                    </div>
                  )}
                /> */}
                <Column
                  caption="Actions"
                  width={100}
                  cellRender={({ data, rowIndex }) => (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteItem(data.id, rowIndex)}
                      title="Delete Item"
                    >
                      <i className="bx bx-trash" />
                    </Button>
                  )}
                />
                <Column caption="Style No." dataField="styleNo" width={120} />
                <Column
                  caption="Category"
                  dataField="categoryName"
                  width={120}
                />
                <Column
                  caption="Dia/Gold Wt"
                  dataField="diaGoldWt"
                  format={{ type: "fixedPoint", precision: 3 }}
                  width={120}
                />
                <Column
                  caption="Dia Qty"
                  dataField="diaQty"
                  width={100}
                  format="fixedPoint"
                />
                <Column
                  caption="Dia Clarity"
                  dataField="clarity"
                  width={120}
                />
                <Column caption="Dia Color" dataField="color" width={120} />
                <Column
                  caption="Net Wt"
                  dataField="netWt"
                  width={100}
                  format={{ type: "fixedPoint", precision: 3 }}
                />
                <Column
                  caption="Gross Wt"
                  dataField="grossWt"
                  width={100}
                  format={{ type: "fixedPoint", precision: 3 }}
                />
                <Column caption="Kt" dataField="karatType" width={80} />
                {/* <Column caption="Qty" dataField="pcs" width={80} /> */}
                <Column caption="IGI No" dataField="igiNo" width={120} />
                <Column caption="HUID No" dataField="huidNo" width={120} />
                <Column caption="Bag No" dataField="bagNo" width={120} />
                <Column
                  caption="MRP"
                  dataField="mrp"
                  width={100}
                  format={{ type: "fixedPoint", precision: 2 }}
                />
                <Column caption="Tag" dataField="tag" width={100} />
                <Column
                  caption="Is Memo"
                  dataField="isSNMCC"
                  width={100}
                  cellRender={({ value }) => (value ? "Yes" : "No")}
                />
                <Column caption="Color Stone Price" dataField="colorStonePrice" width={150} format={{ type: "fixedPoint", precision: 2 }} />

              </DataGrid>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Side Summary */}
        <Col sm={3}>
          <SalesEntrySummary />
          <Row className="mb-3">
            <Col className="d-flex justify-content-center">
              <Button
                className="me-4 add-btn btn-sm py-1"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                className="submit-btn"
                onClick={handleSave}
                disabled={submissionLoading}
              >
                {submissionLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2 submit-btn btn-sm py-1"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      <ConfirmActionModal
        show={showModal}
        onHide={() => setShowModal(false)}
        data={dataTolock}
      />

      <ConfirmActionModal
        show={showEcomModal}
        onHide={() => setShowEcomModal(false)}
        data={dataToEcomlock}
        isEcomLocked={isEcomLocked}
      />

      {/* Modals */}
      <SalesEntryModals
        showItemModal={false}
        setShowItemModal={() => { }}
        editingItem={null}
        showCustomerModal={modalHandlers.customer.show}
        setShowCustomerModal={modalHandlers.customer.onHide}
        showSalesPersonModal={modalHandlers.salesPerson.show}
        setShowSalesPersonModal={modalHandlers.salesPerson.onHide}
        showRedemptionModal={modalHandlers.redemption.show}
        setShowRedemptionModal={modalHandlers.redemption.onHide}
        showRewardPointsModal={modalHandlers.rewardPoints.show}
        setShowRewardPointsModal={modalHandlers.rewardPoints.onHide}
        showCouponModal={modalHandlers.coupon.show}
        setShowCouponModal={modalHandlers.coupon.onHide}
        showDiscountModal={modalHandlers.discount.show}
        setShowDiscountModal={modalHandlers.discount.onHide}
        showPaymentModal={modalHandlers.payment.show}
        setShowPaymentModal={modalHandlers.payment.onHide}
        showOldGoldModal={modalHandlers.oldGold.show}
        setShowOldGoldModal={modalHandlers.oldGold.onHide}
        defaultSearchType="styleNo"
      />

      <ProductSelectionModal
        show={modals.productSelection}
        onHide={() => toggleModal("productSelection", false)}
        products={productSelectionData.products}
        onSelectProduct={addProductToItems}
        onSelectMultipleProducts={addMultipleProductsToItems}
        loading={productDetailsLoading}
        searchType={productSelectionData.searchType}
      />

      <CalculationModal
        show={modals.calculation}
        onHide={() => toggleModal("calculation", false)}
        salesEntryData={state}
      />

      <SubmitConfirmationModal
        show={modals.submitConfirmation}
        onHide={() => toggleModal("submitConfirmation", false)}
        onConfirm={handleConfirmSubmit}
        salesEntryData={state}
        loading={submissionLoading}
      />

      <ExchangeModal
        show={modals.exchange}
        onHide={() => toggleModal("exchange", false)}
      />
    </div>
  );
};

const NewSalesEntry: React.FC = () => {
  const { coid, cid } = useParams<{ coid?: string; cid?: string }>();
  return (
    <div className="page-content">
      <div className="container-fluid">
        {HasPermission('SalesEntry.Write') &&
          <SalesEntryErrorBoundary>
            <SalesEntryProvider coid={coid} cid={cid}>
              <NewSalesEntryContent />
            </SalesEntryProvider>
          </SalesEntryErrorBoundary>
        }
      </div>
    </div>
  );
}

export default NewSalesEntry;